import React, { useCallback, useEffect, useState } from 'react';
import type * as CSS from 'csstype';
import WarningIcon from '../../../assets/warning-sign-24px.png';
import './logic-error.css';
import { EditorService } from '../../../services/editor';
import {
	EntityErrorSeverity,
	EntityError,
	SharedEntityErrorCode,
	DefinitionEntityState,
	EntityState,
	CanvasEntityDisabledReason,
	translateLogicErrorCode,
	translateLogicErrorCodeShort,
	CanvasEntityOtherDisabledReason,
	EntityInstanceErrorCode,
} from '@elyx-code/project-logic-tree';
import { resolveSeverityIcon } from '../../notifications/NotificationsPanel';
import { NotificationSeverity } from '../../../services/notifications';
import CanvasPopup, {
	useCanvasPopup,
} from '../../../services/editor/CanvasPopup';
import { RelativeObjectsState } from '../../../lib/canvas';
import CanvasPopupBaseComponent from '../../../lib/popup/CanvasPopupBaseComponent';
import Badge from '../../../lib/badge/Badge';
import { CanvasEntityElement } from '../../../services/editor/CanvasElement';
import { Logger } from '@elyx-code/common-ts-utils';
import { mergeRefs } from '../../../utils/react';

export interface ILogicErrorIconProps {
	entity: EntityState;
	project: EditorService;
	idSuffix?: string;
	canvasElement: CanvasEntityElement;
	parentDisabledReasons: (
		| CanvasEntityDisabledReason
		| CanvasEntityOtherDisabledReason
	)[];
	style?: CSS.Properties;
	[key: string]: any;
}

export interface ILogicErrorMenuProps {
	errors: EntityError[];
	entity: EntityState;
	project: EditorService;
	draggableObject: RelativeObjectsState;
	canvasElement: CanvasEntityElement;
}

interface IErrorGroup {
	count: number;
	error: EntityError;
}

export interface ILogicErrorMenuItemProps {
	group: IErrorGroup;
	entity: EntityState;
	project: EditorService;
	draggableObject: RelativeObjectsState;
	canvasElement: CanvasEntityElement;
}

function LogicErrorsMenuItem(props: ILogicErrorMenuItemProps) {
	const memoizedErrorCountCanvasPopupBody = useCallback(
		() => (
			<CanvasPopupBaseComponent
				containerClasses={['tooltip', 'tooltip--large-font']}
			>
				{'This error occurs ' + props.group.count + ' times on this entity'}
			</CanvasPopupBaseComponent>
		),
		[props.group.count],
	);

	const canvasErrorCountPopup = useCanvasPopup(
		{
			editor: props.project,
			id:
				'entity-error--dropdown-menu-card-option--' +
				props.group.error.id +
				'--repeated-counter-badge--popup',
			anchorQuerySelector:
				'#entity-error--dropdown-menu-card-option--' +
				props.group.error.id +
				'--repeated-counter-badge',
			arrowClasses: ['tooltip-arrow'],
			parentCanvasObject: props.draggableObject,
			placement: 'top',
		},
		memoizedErrorCountCanvasPopupBody,
	)
		.setupHover({
			closeDelay: 80,
			openDelay: 600,
		})
		.onBeforeShow(() => {
			return props.group.count > 1;
		});

	return (
		<span>
			<button
				onClick={() => {
					Logger.log('Navigate to error');
				}}
				id={'entity-error--dropdown-menu-card-option--' + props.entity.id}
				className='logic-error-dropdown-menu-card-option'
			>
				<i
					className={[
						'fa-solid',
						resolveSeverityIcon(
							props.group.error.severity as any as NotificationSeverity,
						),
					].join(' ')}
				></i>
				<span style={{ display: 'flex', flexDirection: 'column' }}>
					<span style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
						<span
							style={{
								display: 'flex',
								gap: '14px',
								alignItems: 'center',
								fontSize: '18px',
								fontWeight: 600,
							}}
							className='logic-error-dropdown-menu-card-option__name'
						>
							{translateLogicErrorCodeShort(props.group.error)}
						</span>
						{props.group.count > 1 && (
							<Badge
								ref={canvasErrorCountPopup.anchorRef}
								className='canvas-badge-overrides'
								id={
									'entity-error--dropdown-menu-card-option--' +
									props.group.error.id +
									'--repeated-counter-badge'
								}
							>
								{props.group.count.toString()}
							</Badge>
						)}
					</span>
					<span
						style={{
							display: 'flex',
							gap: '14px',
							alignItems: 'center',
							color: 'rgba(0,0,0,0.54)',
							fontSize: '16px',
						}}
					>
						{props.group.error.code === SharedEntityErrorCode.UnreachableEntity
							? props.group.error.message
							: translateLogicErrorCode(props.group.error)}
					</span>
				</span>
			</button>
		</span>
	);
}

function groupErrorsByCode(errors: EntityError[]): IErrorGroup[] {
	return errors.reduce((acc, error) => {
		const existingGroup = acc.find((group) => group.error.code === error.code);
		if (existingGroup) {
			existingGroup.count++;
		} else {
			acc.push({
				error,
				count: 1,
			});
		}
		return acc;
	}, []);
}

function LogicErrorsMenu(props: ILogicErrorMenuProps) {
	// Separate errors from warnings based on the 'severity'
	const errors = props.errors.filter(
		(error) => error.severity === EntityErrorSeverity.Error,
	);
	const warnings = props.errors.filter(
		(error) => error.severity === EntityErrorSeverity.Warning,
	);

	// Group errors and warnings that have the same code
	const errorGroups: IErrorGroup[] = groupErrorsByCode(errors);
	const warningGroups: IErrorGroup[] = groupErrorsByCode(warnings);

	return (
		<div className='dropdown-menu-card entity-error-menu'>
			{!!errorGroups.length && (
				<>
					<header
						style={{
							padding: '10px 20px',
							borderBottom: '1px solid #e0e0e0',
							fontWeight: 400,
							fontSize: '14px',
							color: 'var(--color-main-grey)',
						}}
					>
						Errors
					</header>
					{errorGroups.map((group) => (
						<LogicErrorsMenuItem
							key={group.error.id}
							group={group}
							entity={props.entity}
							project={props.project}
							draggableObject={props.draggableObject}
							canvasElement={props.canvasElement}
						/>
					))}
				</>
			)}
			{!!warnings.length && (
				<>
					<header
						style={{
							padding: '10px 20px',
							borderBottom: '1px solid #e0e0e0',
							fontWeight: 400,
							fontSize: '14px',
							color: 'var(--color-main-grey)',
						}}
					>
						Warnings
					</header>
					{warningGroups.map((group) => (
						<LogicErrorsMenuItem
							key={group.error.id}
							group={group}
							entity={props.entity}
							project={props.project}
							draggableObject={props.draggableObject}
							canvasElement={props.canvasElement}
						/>
					))}
				</>
			)}
		</div>
	);
}

export default function LogicErrorIconMenu(props: ILogicErrorIconProps) {
	const [errors, setErrors] = useState<EntityError[]>(
		props.entity.getShallowErrors(),
	);

	const memoizedCanvasPopupBody = useCallback(
		(popup: CanvasPopup) => (
			<CanvasPopupBaseComponent
				id={
					'logic-error-icon--' +
					props.entity.id +
					'--popup--body' +
					(props.idSuffix || '')
				}
			>
				<LogicErrorsMenu
					errors={errors}
					entity={props.entity}
					project={props.project}
					draggableObject={popup.self}
					canvasElement={props.canvasElement}
				/>
			</CanvasPopupBaseComponent>
		),
		[errors, props.entity, props.project, props.canvasElement, props.idSuffix],
	);

	const {
		canvasElement,
		onRender,
		project,
		entity,
		style,
		idSuffix,
		parentDisabledReasons,
		...otherProps
	} = props;

	const relevantParentDisabledReasons = props.parentDisabledReasons.filter(
		(reason) =>
			![CanvasEntityOtherDisabledReason.CanvasGloballyDisabled].includes(
				// @ts-ignore
				reason,
			),
	);

	const canvasPopup = useCanvasPopup(
		{
			editor: props.project,
			id:
				'logic-error-icon--' +
				props.entity.id +
				'--popup' +
				(props.idSuffix || ''),
			anchorQuerySelector:
				'#logic-error-icon--' + props.entity.id + (props.idSuffix || ''),
			arrowClasses: ['dropdown-menu-card-arrow'],
			parentCanvasObject: props.canvasElement.draggableObject,
			placement: 'top',
			arrowContainerSelector:
				'#logic-error-icon--' +
				props.entity.id +
				'--popup--body' +
				(props.idSuffix || ''),
		},
		memoizedCanvasPopupBody,
		[errors.length],
	)
		.setupHover({
			closeDelay: 80,
			openDelay: 600,
		})
		.onBeforeShow(() => {
			// If the canvas element is currently being dragged,
			// Or it is currently selecting an area, then do not show the popup
			if (
				props.project.canvas?.isSelecting ||
				props.project.canvas?.isMovingObjects ||
				!!relevantParentDisabledReasons.length
			) {
				return false;
			}

			return true;
		});

	useEffect(() => {
		const newErrors = (
			props.entity as DefinitionEntityState
		).getShallowErrors();
		const oldErrors = [...errors];

		// Check if errors have changed based on their ids
		const oldErrorIds = (oldErrors as EntityError[]).map((error) => error.id);
		const newErrorIds = newErrors.map((error) => error.id);

		// If the errors have not changed, then do not update the popup
		if (oldErrorIds.join(',') !== newErrorIds.join(',')) {
			setErrors(newErrors);
		}

		// if (!!errors.length && !canvasPopup && canvasPopupReady) {
		// canvasPopup.refresh();
		// } else if (!errors.length && canvasPopup && canvasPopupReady) {
		// 	canvasPopup.destroy();
		// 	canvasPopup = null;
		// } else if (!!errors.length && canvasPopup && canvasPopupReady) {
		// 	canvasPopup.body((popup) => (
		// 		<LogicErrorsMenu
		// 			errors={errors}
		// 			entity={props.entity}
		// 			project={props.project}
		// 			draggableObject={props.canvasElement.draggableObject}
		// 			canvasElement={props.canvasElement}
		// 		/>
		// 	));
		// }
	});

	// const memoizedBlockedReachabilityCanvasPopupBody = useCallback(
	// 	() => (
	// 		<CanvasPopupBaseComponent
	// 			containerClasses={['tooltip', 'error-background']}
	// 		>
	// 			Unreachable!
	// 		</CanvasPopupBaseComponent>
	// 	),
	// 	[],
	// );

	// const canvasBlockedReachabilityPopup = useCanvasPopup(
	// 	{
	// 		editor: props.project,
	// 		id: 'entity-error--' + props.entity.id + '--reachability-error-popup',
	// 		anchorQuerySelector: '#logic-error-icon--' + entity.id + (idSuffix || ''),
	// 		arrowClasses: ['error-background'],
	// 		parentCanvasObject: props.draggableObject,
	// 		placement: 'top',
	// 	},
	// 	memoizedBlockedReachabilityCanvasPopupBody,
	// ).setup().show(false);

	// const hasBlockedReachabilityError = !!props.entity.errors.find(
	// 	(e) =>
	// 		e.code === EntityInstanceErrorCode.LastCalledEntityMustBeReturnEntity,
	// );

	// if (hasBlockedReachabilityError) {
	// 	console.log('Showing')
	// 	canvasBlockedReachabilityPopup.show(false);
	// }

	// const mergedRefs = mergeRefs(
	// 	canvasBlockedReachabilityPopup.anchorRef,
	// 	canvasPopup.anchorRef,
	// );

	return (
		!!errors.length && (
			<span>
				<img
					ref={canvasPopup.anchorRef}
					{...otherProps}
					style={style}
					id={'logic-error-icon--' + entity.id + (idSuffix || '')}
					src={WarningIcon}
					className={[
						'warning-icon',
						'logic-error-icon',
						...(!!relevantParentDisabledReasons.length ? ['disabled'] : []),
					].join(' ')}
				/>
			</span>
		)
	);
}
